import { React, useState, useEffect } from 'react';
import PageHeader from 'utility/PageHeader';
import PageBody from 'utility/PageBody';
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Table,
  DatePicker,
  Modal,
  Select,
  Upload,
  Image,
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { HttpHandler } from '../../shared/httpHandler';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

// 子類別對應表
const subCategoryMap = {
  1: '聖誕賀卡',
  2: '新年賀卡',
  3: '春節賀卡',
  4: '端午賀卡',
  5: '中秋賀卡',
  99: '其他賀卡',
};

const httpHandler = new HttpHandler();

// 父組件
const Card = () => {
  const [searchForm] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    updateData(pagination);
  };

  const updateData = (pagination = tableParams.pagination) => {
    const { name, subCategory } = searchForm.getFieldsValue();
    const { current, pageSize } = pagination;
    setLoading(true);
    httpHandler
      .get(
        `/line-official-mgmt/card/list?name=${name}&sub_category=${subCategory}&page=${current}&page_size=${pageSize}`
      )
      .then((response) => {
        if (response.success) {
          // 轉換sub_category
          response.data.forEach((item) => {
            item.sub_category = subCategoryMap[item.sub_category];
          });
          setTableData(response.data);
          setTableParams({
            ...tableParams,
            pagination: {
              ...pagination,
              total: response.total,
            },
          });
        } else {
          window.antdNotify('錯誤', response.data.message, 'fail');
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      maskClosable: true, // 點擊蒙層是否允許關閉
      title: '確定刪除？',
      icon: <QuestionCircleOutlined />,
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn loading={deleteLoading} />
        </>
      ),
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        setDeleteLoading(true);
        return httpHandler
          .delete(`/line-official-mgmt/card/${id}`)
          .then((response) => {
            if (response.success) {
              window.antdNotify('成功', '刪除成功', 'success');
              searchCard();
            } else {
              window.antdNotify('錯誤', response.data.message, 'fail');
            }
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            setDeleteLoading(false);
          });
      },
    });
  };
  const searchCard = async () => {
    setTableParams({
      ...tableParams,
      pagination: {
        current: 1,
        pageSize: 10,
      },
    });
    updateData({
      current: 1,
      pageSize: 10,
    });
  };

  useEffect(() => {
    // 預設表單值
    searchForm.setFieldsValue({
      name: '',
      subCategory: '0',
    });
    // 進入畫面時執行一次搜尋
    searchCard();
  }, []);
  // 搜尋組件
  const SearchBar = () => {
    return (
      <>
        <Form form={searchForm} name="searchForm" onFinish={searchCard}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="祝賀卡名稱" name="name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="子類別" name="subCategory">
                <Select>
                  <Select.Option value="0">全部</Select.Option>
                  {Object.keys(subCategoryMap).map((key) => (
                    <Select.Option key={key} value={key}>
                      {subCategoryMap[key]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16} className="button-group">
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => {
                  setIsNew(true);
                  setIsModalVisible(true);
                }}
                className="button button--register">
                上傳
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                htmlType="submit"
                className="button button--search">
                搜尋
              </Button>
            </Col>
          </Row>
        </Form>
      </>
    );
  };
  // 表格組件
  const DataTable = () => {
    let columns = [
      {
        title: 'id',
        dataIndex: 'id',
        hidden: true,
      },
      {
        title: '祝賀卡名稱',
        dataIndex: 'name',
      },
      {
        title: '圖片',
        dataIndex: '',
        render: (text, record, index) => {
          return (
            <Image
              src={`data:image/jpeg;base64,${record.file_contents}`}
              alt="Item"
              style={{
                maxWidth: '100px',
                maxHeight: '100px',
                width: 'auto',
                height: 'auto',
              }} // 設定圖片的最大寬度和高度，並按比例縮放
            />
          );
        },
      },
      {
        title: '操作',
        dataIndex: '',
        render: (text, record, index) => {
          return (
            <>
              <Button
                icon={<EditOutlined />}
                type="primary"
                className="button--edit"
                style={{ marginRight: 16 }}
                onClick={() => {
                  setIsNew(false);
                  modalForm.setFieldsValue({
                    id: record.id,
                    name: `${record.name}.${record.extension_name}`,
                    // 從subCategoryMap找出對應的key
                    subCategory: Object.keys(subCategoryMap).find(
                      (key) => subCategoryMap[key] === record.sub_category
                    ),
                    projectUrl: record.url,
                    content: record.content,
                    startMonthDate: dayjs(record.start_month_date, 'MM-DD'),
                    endMonthDate: dayjs(record.end_month_date, 'MM-DD'),
                    memo: record.memo,
                  });
                  setIsModalVisible(true);
                }}>
                修改
              </Button>
              <Button
                icon={<DeleteOutlined />}
                type="primary"
                className="button--delete"
                onClick={() => {
                  handleDelete(record.id);
                }}>
                刪除
              </Button>
            </>
          );
        },
      },
      {
        title: '子類別',
        dataIndex: 'sub_category',
      },
      {
        title: '備註',
        dataIndex: 'memo',
      },
      {
        title: '修改時間',
        dataIndex: 'update_time',
      },
      {
        title: '修改人員',
        dataIndex: 'update_user',
      },
      {
        title: '建立時間',
        dataIndex: 'create_time',
      },
      {
        title: '建立人員',
        dataIndex: 'create_user',
      },
    ].filter((item) => !item.hidden);

    return (
      <>
        <Table
          columns={columns}
          rowKey={(row) => row.id}
          dataSource={tableData}
          pagination={tableParams.pagination}
          loading={loading}
          bordered="true"
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
        />
      </>
    );
  };

  const section = [
    {
      hasTitle: false,
      title: 'Search',
      children: <SearchBar />,
    },
    {
      hasTitle: false,
      title: 'Data',
      children: <DataTable />,
    },
    {
      hasTitle: false,
      title: 'Modal',
      children: (
        <CardModal
          searchCard={searchCard}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          modalForm={modalForm}
          isNew={isNew}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader functionName={'card'}></PageHeader>
      <PageBody section={section}></PageBody>
    </div>
  );
};
// 共用的Modal
const CardModal = (props) => {
  const { searchCard, isModalVisible, setIsModalVisible, modalForm, isNew } =
    props;
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleModalSubmit = () => {
    modalForm.validateFields().then(async (values) => {
      const confirmed = await window.antdConfirm(
        `確定${isNew ? '新增' : '修改'}祝賀卡嗎？`
      );
      if (!confirmed) return;
      setLoading(true);
      // 組合資料
      const postData = new FormData();
      let data = {
        category: values.category,
        sub_category: values.subCategory,
        start_month_date: dayjs(values.startMonthDate).format('MM-DD'),
        end_month_date: dayjs(values.endMonthDate).format('MM-DD'),
        memo: values.memo,
      };
      postData.append('data', JSON.stringify(data));
      // 如果有更新圖檔，則傳送到後端
      if (fileList.length > 0) {
        postData.append('uploadfile', fileList[0]['originFileObj']);
      } else {
        postData.append('uploadfile', '');
      }
      if (isNew) {
        var handler = httpHandler.post(
          '/line-official-mgmt/card',
          postData,
          'multipart/form-data'
        );
      } else {
        var handler = httpHandler.put(
          `/line-official-mgmt/card/${values.id}`,
          postData,
          'multipart/form-data'
        );
      }

      handler.then((response) => {
        if (response.success) {
          window.antdNotify('成功', isNew ? '新增成功' : '修改成功', 'success');
          handleModalCancel();
          setLoading(false);
          searchCard();
        } else {
          setLoading(false);
          window.antdNotify('錯誤', response.data.message, 'fail');
        }
      });
    });
  };
  const handleModalCancel = () => {
    setFileList([]);
    modalForm.resetFields();
    setIsModalVisible(false);
  };
  return (
    <Modal
      title={isNew ? '上傳祝賀卡' : '修改祝賀卡'}
      open={isModalVisible}
      onCancel={handleModalCancel}
      footer={[
        <Button key="back" onClick={handleModalCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleModalSubmit}
          loading={loading}>
          確定
        </Button>,
      ]}
      okButtonProps={{ loading: loading }}>
      <ModalForm
        modalForm={modalForm}
        fileList={fileList}
        setFileList={setFileList}
        isNew={isNew}
      />
    </Modal>
  );
};
// 共用Modal專用的Form(新增、修改共用)
const ModalForm = (props) => {
  const { modalForm, fileList, setFileList, isNew } = props;
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };
  // 上傳前檢查格式
  const beforeUpload = (file) => {
    if (file.type.startWith('image/')) {
      window.antdNotify('錯誤', '只能上傳圖片檔案', 'fail');
      return false;
    }
    return true;
  };
  // 上傳圖片轉base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  // 預覽圖片
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewTitle(file.name);
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };
  // 取消預覽
  const handleCancel = () => setPreviewVisible(false);

  return (
    <Form form={modalForm}>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="category" hidden initialValue="1">
        <Input />
      </Form.Item>
      <Form.Item
        label="上傳祝賀卡"
        name="file"
        rules={
          isNew
            ? [
                {
                  required: true,
                  message: '請上傳祝賀卡',
                  validator: (_, value) => {
                    if (fileList.length > 0) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(new Error('請上傳祝賀卡'));
                    }
                  },
                },
              ]
            : []
        }>
        <Upload
          fileList={fileList}
          onChange={handleChange}
          listType="picture"
          beforeUpload={beforeUpload}
          onPreview={handlePreview}
          maxCount={1}>
          <Button icon={<PlusOutlined />}>上傳</Button>
        </Upload>
      </Form.Item>
      {/* 預覽Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}>
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      <Form.Item
        label="子類別"
        name="subCategory"
        rules={[
          {
            required: true,
            message: '請選擇子類別',
          },
        ]}>
        <Select>
          {Object.keys(subCategoryMap).map((key) => (
            <Select.Option key={key} value={key}>
              {subCategoryMap[key]}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="生效日期"
        name="startMonthDate"
        rules={[
          {
            required: true,
            message: '請輸入生效日期',
          },
        ]}>
        <DatePicker format="MM-DD" />
      </Form.Item>
      <Form.Item
        label="失效日期"
        name="endMonthDate"
        rules={[
          {
            required: true,
            message: '請輸入失效日期',
          },
        ]}>
        <DatePicker format="MM-DD" />
      </Form.Item>
      <Form.Item label="備註" name="memo">
        <Input />
      </Form.Item>
    </Form>
  );
};

// 驗證props
CardModal.propTypes = {
  searchCard: PropTypes.func,
  isModalVisible: PropTypes.bool,
  setIsModalVisible: PropTypes.func,
  modalForm: PropTypes.object,
};
ModalForm.propTypes = {
  isNew: PropTypes.bool,
  modalForm: PropTypes.object,
  fileList: PropTypes.array,
  setFileList: PropTypes.func,
};
export default Card;
